import {
  animate,
  AnimationBuilder,
  AnimationPlayer,
  style,
} from '@angular/animations';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import { RegistryService } from '@core/utils';
import { ReplaySubject } from 'rxjs';
import {
  NavigationAppearance,
  NavigationItem,
  NavigationMode,
  NavigationPosition,
} from '../../common/navigation/navigation.types';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidebarComponent implements OnInit, OnChanges, OnDestroy {
  @Input() appearance: NavigationAppearance = 'dense';
  @Input() autoCollapse = true;
  @Input() mode: NavigationMode = 'side';
  @Input() navigation: NavigationItem[] = [];
  @Input() opened = true;
  @Input() hovered = false;
  @Input() position: NavigationPosition = 'left';
  @Output()
  readonly appearanceChanged = new EventEmitter<NavigationAppearance>();
  @Output() readonly modeChanged = new EventEmitter<NavigationMode>();
  @Output() readonly openedChanged = new EventEmitter<boolean>();
  @Output() readonly positionChanged = new EventEmitter<NavigationPosition>();
  @Output() readonly hoveredChanged = new EventEmitter<boolean>();

  readonly sidebarName = 'sidebar';
  onCollapsableItemCollapsed = new ReplaySubject<NavigationItem>(1);
  onCollapsableItemExpanded = new ReplaySubject<NavigationItem>(1);

  private _overlay: HTMLElement;
  private _player: AnimationPlayer;
  private _animationsEnabled = false;
  // @ViewChild('navigationContent') private _navigationContentEl: ElementRef;

  constructor(
    private _changeDetectorRef: ChangeDetectorRef,
    private _registry: RegistryService,
    private _renderer2: Renderer2,
    private _elementRef: ElementRef,
    private _animationBuilder: AnimationBuilder,

  ) {}

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Host binding for component classes
   */
  @HostBinding('class') get classList(): any {
    return {
      'fuse-vertical-navigation-animations-enabled': this._animationsEnabled,
      // 'fuse-vertical-navigation-animations-enabled': true,
      [`fuse-vertical-navigation-appearance-${this.appearance}`]: true,
      'fuse-vertical-navigation-hover': this.hovered,
      // 'fuse-vertical-navigation-inner': this.inner,
      'fuse-vertical-navigation-mode-over': this.mode === 'over',
      'fuse-vertical-navigation-mode-side': this.mode === 'side',
      'fuse-vertical-navigation-opened': this.opened,
      'fuse-vertical-navigation-position-left': this.position === 'left',
      'fuse-vertical-navigation-position-right': this.position === 'right',
    };
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On changes
   *
   * @param changes
   */
  ngOnChanges(changes: SimpleChanges): void {
    // console.log(changes);
    // Appearance
    if ('appearance' in changes) {
      // Execute the observable
      this.appearanceChanged.next(changes.appearance.currentValue);
    }

    if ('hovered' in changes) {
      const previousHovered = changes.hovered.previousValue;
      const currentHovered = changes.hovered.currentValue;

      // If the mode changes: 'hovered -> !hovered' then hide overlay
      if (previousHovered === true && currentHovered === false) {
        this._hideOverlay();
      }
      // If the mode changes: '!hovered -> hovered' then show overlay
      if (previousHovered === false && currentHovered === true) {
        this._showOverlay();
      }

      // Enable the animations after a delay
      // The delay must be bigger than the current transition-duration
      // to make sure nothing will be animated while the mode is changing
      setTimeout(() => {
        this._enableAnimations();
      }, 500);
    }

    // Navigation
    if ('navigation' in changes) {
      // Mark for check
      this._changeDetectorRef.markForCheck();
    }

    // Opened
    // if ('opened' in changes) {
    //   // Coerce the value to a boolean
    //   this.opened = !!changes.opened.currentValue;

    //   // Open/close the navigation
    //   this._toggleOpened(this.opened);
    // }

    // Position
    if ('position' in changes) {
      // Execute the observable
      this.positionChanged.next(changes.position.currentValue);
    }

    // Transparent overlay
    // if ('transparentOverlay' in changes) {
    //   // Coerce the value to a boolean
    //   this.transparentOverlay = coerceBooleanProperty(
    //     changes.transparentOverlay.currentValue
    //   );
    // }
  }

  ngOnInit(): void {
    // Register the navigation component
    this._registry.registerComponent(this.sidebarName, this);
  }

  ngOnDestroy(): void {
    this._registry.deregisterComponent(this.sidebarName);
  }

  /**
   * Track by function for ngFor loops
   *
   * @param index
   * @param item
   */
  trackByFn(index: number, item: any): any {
    return item.id || index;
  }

    // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Open the drawer
   */
  open(): void {
    // Return if the drawer has already opened
    if (this.hovered) {
      return;
    }

    // Open the drawer
    this._toggleHovered(true);
  }

  /**
   * Close the drawer
   */
  close(): void {
    // Return if the drawer has already closed
    if (!this.hovered) {
      return;
    }

    // Close the drawer
    this._toggleHovered(false);
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Private methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Enable the animations
   *
   * @private
   */
  private _enableAnimations(): void {
    // Return if the animations are already enabled
    if (this._animationsEnabled) {
      return;
    }

    // Enable the animations
    this._animationsEnabled = true;
  }

  /**
   * Show the overlay
   *
   * @private
   */
  private _showOverlay(): void {
    // Create the overlay element
    this._overlay = this._renderer2.createElement('div');

    // Return if overlay couldn't be create for some reason
    if (!this._overlay) {
      return;
    }

    // Add a class to the overlay element
    this._overlay.classList.add('fuse-vertical-navigation-overlay');

    // Append the overlay to the parent of the navigation
    this._renderer2.appendChild(
      this._elementRef.nativeElement.parentElement,
      this._overlay
    );

    // Create the enter animation and attach it to the player
    this._player = this._animationBuilder
      .build([
        animate(
          '300ms cubic-bezier(0.25, 0.8, 0.25, 1)',
          style({ opacity: 1 })
        ),
      ])
      .create(this._overlay);

    // Play the animation
    this._player.play();

    // Add an event listener to the overlay
    this._overlay.addEventListener('click', () => {
      this.close();
    });
  }

  /**
   * Hide the overlay
   *
   * @private
   */
  private _hideOverlay(): void {
    // Prevent if doesn't exists
    // if (!this._overlay) {
    //   return;
    // }
    // Create the leave animation and attach it to the player
    this._player = this._animationBuilder
      .build([
        animate(
          '300ms cubic-bezier(0.25, 0.8, 0.25, 1)',
          style({ opacity: 0 })
        ),
      ])
      .create(this._overlay);

    // Play the animation
    this._player.play();

    // Once the animation is done...
    this._player.onDone(() => {
      // Destroy the player
      this._player.destroy();
      this._player = null;
      // If the overlay still exists...
      if (this._overlay) {
        // Remove the overlay
        this._overlay.parentNode.removeChild(this._overlay);
        this._overlay = null;
      }
    });
  }

  /**
   * Open/close the drawer
   *
   * @param hovered
   * @private
   */
    private _toggleHovered(hovered: boolean): void {
      // Set the opened
      this.hovered = hovered;

      // Enable the animations
      this._enableAnimations();

      // If the drawer opens, show the overlay
      if (hovered) {
        this._showOverlay();
      }
      // Otherwise, close the overlay
      else {
        this._hideOverlay();
      }

      // Execute the observable
      this.hoveredChanged.next(hovered);
    }
}
